/**
 * Sentry error monitoring — frontend bootstrap.
 *
 * Becomes a no-op when REACT_APP_SENTRY_DSN is unset, so production error
 * tracking lights up the moment the DSN is added to /app/frontend/.env.
 *
 * Privacy posture:
 *   - User email/name stripped from events (in `beforeSend`)
 *   - Authorization / Cookie headers stripped from request context
 *   - Query strings trimmed off URLs (may contain session_ids / PII)
 *   - No performance traces, no replays
 */
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";

const SENSITIVE_HEADERS = new Set([
  "authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
]);

function stripHeaders(headers) {
  if (!headers || typeof headers !== "object") return headers;
  const out = { ...headers };
  Object.keys(out).forEach((k) => {
    if (SENSITIVE_HEADERS.has(k.toLowerCase())) delete out[k];
  });
  return out;
}

function stripQueryString(url) {
  if (typeof url !== "string") return url;
  const i = url.indexOf("?");
  return i === -1 ? url : url.slice(0, i);
}

function beforeSend(event, hint) {
  // Scrub user PII
  if (event.user) {
    delete event.user.email;
    delete event.user.username;
    delete event.user.ip_address;
  }

  // Scrub request headers + query string
  if (event.request) {
    event.request.headers = stripHeaders(event.request.headers);
    event.request.url = stripQueryString(event.request.url);
  }

  // Enrich axios errors with safe response metadata
  const orig = hint && hint.originalException;
  if (orig && isAxiosError(orig) && orig.response) {
    const resp = orig.response;
    const safeBody = { ...(resp.data || {}) };
    delete safeBody.token;
    delete safeBody.access_token;
    delete safeBody.refresh_token;
    delete safeBody.password;
    event.contexts = {
      ...(event.contexts || {}),
      Axios: {
        status: resp.status,
        method: resp.config?.method,
        url: stripQueryString(resp.config?.url),
        response_excerpt: safeBody,
      },
    };
  }
  return event;
}

function beforeBreadcrumb(breadcrumb) {
  // Drop very noisy interaction breadcrumbs
  if (breadcrumb.category === "ui.click") return null;
  // Trim query strings on HTTP breadcrumbs
  if (breadcrumb.data && typeof breadcrumb.data.url === "string") {
    breadcrumb.data.url = stripQueryString(breadcrumb.data.url);
  }
  return breadcrumb;
}

/** Initialise Sentry. Returns true when DSN is configured, else false (no-op). */
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    // Error monitoring only — no traces, no replays
    integrations: [],
    tracesSampleRate: 0.0,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.0,
    sendDefaultPii: false,
    beforeSend,
    beforeBreadcrumb,
  });
  return true;
}

export { Sentry };
